/**
 * One view context (filtering audit section 4, Phase B step 1): what the
 * person is looking at, the same object on every screen, in the URL, in
 * storage and in every request. See docs/view-context.md for the contract
 * the next steps build on.
 *
 * ViewContext {
 *   venues:      { ids, label, origin: default | picker | group | org | link, orgId, filter, link? }
 *   focus:       { kind: outlet, id, label, screen } | null   // a screen's local drill-down
 *   period:      { kind: frame | range, start, end, inProgress, aggregation, frameCount, frameUnit }
 *   comparison:  { kind: none | previous | last-year-weekdays | last-year-dates | custom, start, end, explicit }
 *   filters:     { statuses, sources, shifts }
 *   definitions: performance-status | growth-outcomes   // declared by the screen, not chosen
 * }
 *
 * This module is pure: types, the screens' capability declarations, the
 * client mirror of the server's period and comparison windows, URL and
 * storage helpers. The React store lives in hooks/useViewContext.ts.
 */
import type { GrowthSubview } from '../data/growthTypes';
import type {
  PerfCompareKind,
  PerfSettings,
  ReservationSourceId,
  ReservationStatusGroup,
} from '../types';
import {
  coercePerfSettings,
  DEFAULT_COMPARE_KIND,
  DEFAULT_PERF_SETTINGS,
  effectiveCompareKind,
  isCompareDateRange,
  isDateRange,
  yearOverYearRange,
} from './perfSettings';
import { decodeOpsUrlState } from './opsUrlState';
import {
  filterForVenues,
  isOrgFilter,
  isOutletFilter,
  isVenuesFilter,
  scopeGroupIdFromFilter,
  venueIdsFromFilter,
} from './scopeFilter';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const VIEW_CONTEXT_VERSION = 1;

/** The parts a screen can use; each one is a chip in the context bar (step 3). */
export const CONTEXT_PARTS = [
  'venues',
  'period',
  'comparison',
  'statuses',
  'sources',
  'shifts',
] as const;
export type ContextPart = (typeof CONTEXT_PARTS)[number];

export type ComparisonKind =
  | 'none'
  | 'previous'
  | 'last-year-weekdays'
  | 'last-year-dates'
  | 'custom';

/**
 * Where the venues come from: `default` the opened venue alone (nothing
 * chosen, decision 7.7.1), `picker` one venue or a set picked, `group` one of
 * the person's own groups, `org` "All venues of <group>", `link` a shared
 * link's venues shown to someone else (decision 7.7.2).
 */
export type VenueOrigin = 'default' | 'picker' | 'group' | 'org' | 'link';

/** How a screen counts: Performance's status groups or Growth's fixed outcomes. */
export type Definitions = 'performance-status' | 'growth-outcomes';

export type GrowthScreenId = `growth:${GrowthSubview}`;

/** Every screen a request can come from; Growth has one per section. */
export type ScreenId =
  | 'assistant'
  | 'performance'
  | 'performance-drill'
  | 'live'
  | 'forecast'
  | 'settings'
  | 'hotel'
  | GrowthScreenId;

/** The context's comparison names, one per settings kind (same meaning everywhere). */
export const COMPARISON_KIND_OF: Record<PerfCompareKind, ComparisonKind> = {
  off: 'none',
  prior: 'previous',
  yoy_weekday: 'last-year-weekdays',
  yoy: 'last-year-dates',
  custom: 'custom',
};

export const PERF_COMPARE_KIND_OF: Record<ComparisonKind, PerfCompareKind> = {
  none: 'off',
  previous: 'prior',
  'last-year-weekdays': 'yoy_weekday',
  'last-year-dates': 'yoy',
  custom: 'custom',
};

export const DEFAULT_COMPARISON: ComparisonKind = COMPARISON_KIND_OF[DEFAULT_COMPARE_KIND];

// ---------------------------------------------------------------------------
// The state the store owns, and the contract built from it
// ---------------------------------------------------------------------------

/** What the person chose; everything else in the context is derived. */
export type ViewContextState = {
  /** Topbar scope: `all` | `group:<id>` | `outlet:<id>`. */
  regionFilter: string | null;
  /** Period and comparison (plus chart grain and capacity basis). */
  perfSettings: PerfSettings;
  status: ReservationStatusGroup[];
  source: ReservationSourceId[];
  shifts: string[];
};

export const DEFAULT_VIEW_CONTEXT_STATE: ViewContextState = {
  regionFilter: 'all',
  perfSettings: DEFAULT_PERF_SETTINGS,
  status: [],
  source: [],
  shifts: [],
};

/**
 * What a shared link asked for and what it shows (decision 7.7.2): the
 * venue ids it carried, the ones shown (the recipient can open them), the
 * ones dropped, and whether it named a group that is not the recipient's.
 * Step 3's context bar says it plainly; until the person changes the venues.
 */
export type ViewContextLink = {
  requested: string[];
  shown: string[];
  dropped: string[];
  groupNotYours: boolean;
  groupId: string | null;
};

export type ViewContextVenues = {
  ids: string[];
  label: string;
  origin: VenueOrigin;
  orgId: string | null;
  /** The Topbar filter the ids come from. */
  filter: string | null;
  /** Present while a shared link's venues are on screen. */
  link?: ViewContextLink;
};

/**
 * A screen's local drill-down inside the venues, which never changes them:
 * Live's outlet focus and Forecast's "Showing" (audit B4). Step 3 shows it
 * as a breadcrumb, "Context › Hegibachplatz".
 */
export type ViewContextFocus = {
  kind: 'outlet';
  id: string;
  label: string;
  screen: ScreenId;
};

export type ViewContextPeriod = {
  kind: 'frame' | 'range';
  start: string;
  end: string;
  /** The period reaches today, so its last days have no outcome yet. */
  inProgress: boolean;
  aggregation: PerfSettings['aggregation'];
  frameCount: number;
  frameUnit: PerfSettings['frameUnit'];
};

export type ViewContextComparison = {
  kind: ComparisonKind;
  start: string | null;
  end: string | null;
  /** The person chose it (on any screen); otherwise it is the default. */
  explicit: boolean;
};

export type ViewContext = {
  venues: ViewContextVenues;
  focus: ViewContextFocus | null;
  period: ViewContextPeriod;
  comparison: ViewContextComparison;
  filters: {
    statuses: ReservationStatusGroup[];
    sources: ReservationSourceId[];
    shifts: string[];
  };
  definitions: Definitions;
};

/**
 * A screen's own window (Phase C): Forecast's outlook ("Next 30 days" and
 * its dates) and Live's today. It travels with the context to maître d'AI,
 * so a conversation started there reads that window.
 */
export type ViewContextWindow = {
  kind: 'forecast' | 'live';
  start: string | null;
  end: string | null;
  label?: string | null;
  key?: string | null;
};

/** What every request sends: the full context, the screen and what it uses. */
export type RequestViewContext = ViewContext & {
  v: typeof VIEW_CONTEXT_VERSION;
  screen: ScreenId;
  uses: Record<ContextPart, boolean>;
  window?: ViewContextWindow | null;
};

// ---------------------------------------------------------------------------
// Screen capability declarations
// ---------------------------------------------------------------------------

export type ScreenCapabilities = {
  uses: Record<ContextPart, boolean>;
  definitions: Definitions;
};

const uses = (parts: ContextPart[]): Record<ContextPart, boolean> =>
  Object.fromEntries(CONTEXT_PARTS.map(part => [part, parts.includes(part)])) as Record<
    ContextPart,
    boolean
  >;

const ALL_PARTS = uses([...CONTEXT_PARTS]);
// Growth's analysis sections read every part (decision 7.5.2): statuses map
// onto its outcomes (confirmed, seated and done are completed; the waitlist
// and rejected requests are none), sources and shifts narrow every
// reservation figure as on Performance. Google's figures stay whole.
const GROWTH_ANALYTICS = ALL_PARTS;

/**
 * Which context parts each screen uses and how it counts. A part a screen
 * does not use is never applied there and is not sent as a method argument
 * or a maître d'AI filter from it; the context bar (step 3) shows it muted,
 * "not used here".
 */
export const SCREEN_CAPABILITIES: Record<ScreenId, ScreenCapabilities> = {
  // maître d'AI home and its notebook read the whole context (Phase C binds
  // answers to it).
  assistant: { uses: ALL_PARTS, definitions: 'performance-status' },
  performance: { uses: ALL_PARTS, definitions: 'performance-status' },
  'performance-drill': { uses: ALL_PARTS, definitions: 'performance-status' },
  // Live is tonight's service: no period, and it compares with the usual
  // service of the weekday on its own, not with the chosen comparison.
  live: {
    uses: uses(['venues', 'statuses', 'sources', 'shifts']),
    definitions: 'performance-status',
  },
  // Forecast has its own window and its own "last year at this point"; it
  // counts every booked reservation (audit B5: it showed and ignored status
  // and source).
  forecast: { uses: uses(['venues', 'shifts']), definitions: 'performance-status' },
  // The settings audit reads the venues over the period, which the screen
  // does not show yet (audit 3.2; step 3 shows it or drops it).
  settings: { uses: uses(['venues', 'period']), definitions: 'performance-status' },
  // Hotel reads fixed windows (the next 14 nights, the last 90 and 180 days).
  hotel: { uses: uses(['venues']), definitions: 'performance-status' },
  'growth:sources': { uses: GROWTH_ANALYTICS, definitions: 'growth-outcomes' },
  'growth:overview': { uses: GROWTH_ANALYTICS, definitions: 'growth-outcomes' },
  'growth:guests': { uses: GROWTH_ANALYTICS, definitions: 'growth-outcomes' },
  'growth:website': { uses: GROWTH_ANALYTICS, definitions: 'growth-outcomes' },
  'growth:local': { uses: GROWTH_ANALYTICS, definitions: 'growth-outcomes' },
  // Campaigns are measured over their own dates against their own baseline.
  'growth:campaigns': { uses: uses(['venues']), definitions: 'growth-outcomes' },
  'growth:health': { uses: uses(['venues', 'period']), definitions: 'growth-outcomes' },
  'growth:setup': { uses: uses(['venues']), definitions: 'growth-outcomes' },
  // "Your Google accounts" sits outside any restaurant's context: it uses
  // no part of it, and the bar shows one static chip instead.
  'growth:accounts': { uses: uses([]), definitions: 'growth-outcomes' },
};

export function screenCapabilities(screen: ScreenId): ScreenCapabilities {
  return SCREEN_CAPABILITIES[screen] ?? SCREEN_CAPABILITIES.performance;
}

export function usesContextPart(screen: ScreenId, part: ContextPart): boolean {
  return screenCapabilities(screen).uses[part];
}

/** The screen a mode, Growth section and open drill stand for. */
export function screenIdFor(args: {
  mode: 'live' | 'performance' | 'settings' | 'growth' | 'horizon' | 'hotel' | 'assistant';
  growthView?: GrowthSubview | null;
  drillOpen?: boolean;
}): ScreenId {
  switch (args.mode) {
    case 'live':
      return 'live';
    case 'horizon':
      return 'forecast';
    case 'hotel':
      return 'hotel';
    case 'settings':
      return 'settings';
    case 'assistant':
      return 'assistant';
    case 'growth':
      return `growth:${args.growthView ?? 'sources'}`;
    default:
      return args.drillOpen ? 'performance-drill' : 'performance';
  }
}

function samePeriod(a: PerfSettings, b: PerfSettings): boolean {
  return (
    a.aggregation === b.aggregation &&
    a.frameCount === b.frameCount &&
    a.frameUnit === b.frameUnit &&
    (a.dateStart ?? null) === (b.dateStart ?? null) &&
    (a.dateEnd ?? null) === (b.dateEnd ?? null)
  );
}

/** The parts set away from their default: what a "not used here" chip is about. */
export function activeContextParts(state: ViewContextState): ContextPart[] {
  const active: ContextPart[] = [];
  if (state.regionFilter && state.regionFilter !== 'all') active.push('venues');
  if (!samePeriod(coercePerfSettings(state.perfSettings), DEFAULT_PERF_SETTINGS)) active.push('period');
  if (state.perfSettings.compareExplicit && effectiveCompareKind(state.perfSettings) !== DEFAULT_COMPARE_KIND) {
    active.push('comparison');
  }
  if (state.status.length) active.push('statuses');
  if (state.source.length) active.push('sources');
  if (state.shifts.length) active.push('shifts');
  return active;
}

/**
 * Parts the person set that this screen does not use. Step 3 shows each as
 * a quiet chip, e.g. "Status: Done · not used on Forecast", never silently.
 */
export function unusedActiveParts(screen: ScreenId, state: ViewContextState): ContextPart[] {
  return activeContextParts(state).filter(part => !usesContextPart(screen, part));
}

// ---------------------------------------------------------------------------
// Period and comparison windows (mirror of drill_math.frame_range)
// ---------------------------------------------------------------------------

type Ymd = { y: number; m: number; d: number };

function parseIso(iso: string): Ymd {
  const [y, m, d] = iso.split('-').map(Number);
  return { y: y!, m: m!, d: d! };
}

function toIso({ y, m, d }: Ymd): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function fromUtc(date: Date): Ymd {
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
}

function addDays(day: Ymd, n: number): Ymd {
  return fromUtc(new Date(Date.UTC(day.y, day.m - 1, day.d + n)));
}

/** moment-style month arithmetic: the day clamps to the end of a shorter month. */
function addMonths(day: Ymd, n: number): Ymd {
  const index = day.y * 12 + (day.m - 1) + n;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { y, m, d: Math.min(day.d, last) };
}

function isoWeekday(day: Ymd): number {
  // Monday 1 … Sunday 7.
  return ((new Date(Date.UTC(day.y, day.m - 1, day.d)).getUTCDay() + 6) % 7) + 1;
}

function startOfBucket(day: Ymd, agg: PerfSettings['aggregation']): Ymd {
  if (agg === 'day') return day;
  if (agg === 'week') return addDays(day, 1 - isoWeekday(day));
  if (agg === 'quarter') return { y: day.y, m: Math.floor((day.m - 1) / 3) * 3 + 1, d: 1 };
  if (agg === 'year') return { y: day.y, m: 1, d: 1 };
  return { y: day.y, m: day.m, d: 1 };
}

function endOfBucket(day: Ymd, agg: PerfSettings['aggregation']): Ymd {
  if (agg === 'day') return day;
  if (agg === 'week') return addDays(startOfBucket(day, 'week'), 6);
  if (agg === 'quarter') return addDays(addMonths(startOfBucket(day, 'quarter'), 3), -1);
  if (agg === 'year') return { y: day.y, m: 12, d: 31 };
  return addDays(addMonths(startOfBucket(day, 'month'), 1), -1);
}

function lastCompletedBucketEnd(today: Ymd, agg: PerfSettings['aggregation']): Ymd {
  if (agg === 'day') return addDays(today, -1);
  if (agg === 'week') return endOfBucket(addDays(today, -7), 'week');
  if (agg === 'quarter') return endOfBucket(addMonths(today, -3), 'quarter');
  if (agg === 'year') return endOfBucket(addMonths(today, -12), 'year');
  return endOfBucket(addMonths(today, -1), 'month');
}

function addFrame(day: Ymd, count: number, unit: PerfSettings['frameUnit']): Ymd {
  if (unit === 'day') return addDays(day, count);
  if (unit === 'week') return addDays(day, count * 7);
  if (unit === 'month') return addMonths(day, count);
  return addMonths(day, count * 12);
}

function daysBetween(a: Ymd, b: Ymd): number {
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000);
}

/** Today in the browser's calendar (the venues' day for the people using them). */
export function localToday(now: Date = new Date()): string {
  return toIso({ y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() });
}

/** The period's dates, as the server's frame_range resolves them. */
export function resolvePeriodWindow(settings: PerfSettings, today: string = localToday()): ViewContextPeriod {
  const s = coercePerfSettings(settings);
  const base = { aggregation: s.aggregation, frameCount: s.frameCount, frameUnit: s.frameUnit };
  if (isDateRange(s)) {
    return { kind: 'range', start: s.dateStart!, end: s.dateEnd!, inProgress: s.dateEnd! >= today, ...base };
  }
  const end = lastCompletedBucketEnd(parseIso(today), s.aggregation);
  const start = startOfBucket(addFrame(addDays(end, 1), -s.frameCount, s.frameUnit), s.aggregation);
  return { kind: 'frame', start: toIso(start), end: toIso(end), inProgress: false, ...base };
}

/**
 * The comparison's dates, as the server computes them: the previous period
 * of the same length, last year by weekday (364 days back), last year by
 * date (the calendar), custom dates, or none.
 */
export function resolveComparisonWindow(
  settings: PerfSettings,
  period: ViewContextPeriod = resolvePeriodWindow(settings),
): ViewContextComparison {
  const kind = effectiveCompareKind(settings);
  const explicit = Boolean(settings.compareExplicit);
  const out = (start: string | null, end: string | null): ViewContextComparison => ({
    kind: COMPARISON_KIND_OF[kind],
    start,
    end,
    explicit,
  });
  if (kind === 'off') return out(null, null);
  if (kind === 'custom') {
    return isCompareDateRange(settings)
      ? out(settings.compareDateStart!, settings.compareDateEnd!)
      : { kind: 'none', start: null, end: null, explicit };
  }
  if (kind === 'yoy' || kind === 'yoy_weekday') {
    const range = yearOverYearRange(kind, period.start, period.end);
    return out(range.start, range.end);
  }
  // Previous period: ends the day before the period starts.
  const start = parseIso(period.start);
  const compareEnd = addDays(start, -1);
  const compareStart =
    period.kind === 'range'
      ? addDays(start, -(daysBetween(start, parseIso(period.end)) + 1))
      : startOfBucket(addFrame(start, -period.frameCount, period.frameUnit), period.aggregation);
  return out(toIso(compareStart), toIso(compareEnd));
}

// ---------------------------------------------------------------------------
// Building the context and the request payload
// ---------------------------------------------------------------------------

/** Where the Topbar's venues come from. */
export function venueOriginOf(regionFilter: string | null | undefined): VenueOrigin {
  if (!regionFilter || regionFilter === 'all') return 'default';
  if (isOrgFilter(regionFilter)) return 'org';
  if (isOutletFilter(regionFilter) || isVenuesFilter(regionFilter)) return 'picker';
  return scopeGroupIdFromFilter(regionFilter) ? 'group' : 'picker';
}

export function buildViewContext(
  state: ViewContextState,
  args: {
    screen: ScreenId;
    venues: Omit<ViewContextVenues, 'filter' | 'origin' | 'link'> & {
      origin?: VenueOrigin;
      filter?: string | null;
      link?: ViewContextLink | null;
    };
    focus?: ViewContextFocus | null;
    today?: string;
  },
): ViewContext {
  const period = resolvePeriodWindow(state.perfSettings, args.today);
  const link = args.venues.link ?? null;
  return {
    venues: {
      ids: [...new Set(args.venues.ids)],
      label: args.venues.label,
      origin: args.venues.origin ?? (link ? 'link' : venueOriginOf(state.regionFilter)),
      orgId: args.venues.orgId,
      filter: args.venues.filter === undefined ? state.regionFilter : args.venues.filter,
      ...(link ? { link } : {}),
    },
    focus: args.focus ?? null,
    period,
    comparison: resolveComparisonWindow(state.perfSettings, period),
    filters: { statuses: [...state.status], sources: [...state.source], shifts: [...state.shifts] },
    definitions: screenCapabilities(args.screen).definitions,
  };
}

export function requestViewContext(
  ctx: ViewContext,
  screen: ScreenId,
  window: ViewContextWindow | null = null,
): RequestViewContext {
  return {
    v: VIEW_CONTEXT_VERSION,
    screen,
    ...ctx,
    uses: { ...screenCapabilities(screen).uses },
    ...(window ? { window } : {}),
  };
}

/**
 * The filters maître d'AI's tools apply today (its legacy `source`,
 * `shift` and `status` fields): only the ones the screen uses, so an answer
 * never counts with a filter the screen next to it ignores (audit 3.3). The
 * full context still rides along in `viewContext`.
 */
export function askFilters(
  screen: ScreenId,
  state: { status: readonly string[]; source: readonly string[]; shifts: readonly string[] },
): { source: string[] | null; shift: string[] | null; status: string[] | null } {
  const used = screenCapabilities(screen).uses;
  return {
    source: used.sources && state.source.length ? [...state.source] : null,
    shift: used.shifts && state.shifts.length ? [...state.shifts] : null,
    status: used.statuses && state.status.length ? [...state.status] : null,
  };
}

// ---------------------------------------------------------------------------
// URL
// ---------------------------------------------------------------------------

/** URL params that carry context parts (opsUrlState encodes and decodes them). */
export const VIEW_CONTEXT_URL_PARAMS = [
  's',
  'sv',
  'c',
  'src',
  'st',
  'sh',
  'agg',
  'fc',
  'fu',
  'ds',
  'de',
  'cb',
  'cm',
  'cds',
  'cde',
  // An old Growth link's venue (decision 7.7.1), read as context venues.
  'gp',
] as const;

/**
 * Written into every URL the app writes: the URL then describes the whole
 * context, and a part it leaves out is that part's default. Without it (a
 * bare link, an internal redirect such as `?m=growth&gv=setup`) only the
 * parts a link names count as the link's.
 */
export const VIEW_CONTEXT_URL_MARKER = 'vc';

export function urlCarriesViewContext(search: string): boolean {
  const params = new URLSearchParams(search);
  if (params.has(VIEW_CONTEXT_URL_MARKER)) return true;
  return VIEW_CONTEXT_URL_PARAMS.some(key => params.has(key));
}

/**
 * The venues a link names (decision 7.7.2): its filter as written, the
 * venue ids it carried (`sv` for a group or "All venues of", the ids of a
 * set of venues, the outlet) and the group it named. Null when the URL is no
 * link or names no venues.
 */
export type LinkVenues = { filter: string; ids: string[]; groupId: string | null };

export function linkVenuesFromUrl(search: string): LinkVenues | null {
  if (!urlCarriesViewContext(search)) return null;
  const decoded = decodeOpsUrlState(search);
  if (decoded.compareFilters.length >= 2) return null;
  const filter = decoded.regionFilter ?? 'all';
  if (!filter || filter === 'all') return null;
  const groupId = scopeGroupIdFromFilter(filter);
  const ids = decoded.scopeVenueIds?.length
    ? decoded.scopeVenueIds
    : isVenuesFilter(filter)
      ? venueIdsFromFilter(filter)
      : isOutletFilter(filter)
        ? [filter.slice('outlet:'.length)]
        : [];
  return { filter, ids, groupId };
}

/**
 * The venue filter a link shows at first: a group or "All venues of" with
 * the venues it carried reads as those venues (a private group means nothing
 * to its recipient); the owner's own group comes back once their groups load.
 */
export function linkRegionFilter(link: LinkVenues | null, fallback: string | null): string | null {
  if (!link) return fallback;
  if ((link.groupId || isOrgFilter(link.filter)) && link.ids.length) {
    return filterForVenues(link.ids) ?? fallback;
  }
  return link.filter;
}

/**
 * What a link's venues show to the person opening it, once their groups and
 * the venues they can open are known (decision 7.7.2):
 *
 * - their own group, when the link names one of theirs;
 * - "All venues of <group>" when the link carried exactly those venues;
 * - otherwise the venues the link carried that they can open, one as an
 *   outlet, several as a set; none at all reads as the restaurant opened.
 *
 * `openable` is null when every venue may be opened (aleno staff).
 */
export function resolveLinkVenues(args: {
  link: LinkVenues;
  ownGroups: { id: string; restaurantIds: string[] }[];
  orgVenueIds: string[] | null;
  openable: Set<string> | null;
}): { regionFilter: string; facts: ViewContextLink } {
  const { link, openable } = args;
  const requested = [...new Set(link.ids)];
  const canOpen = (id: string) => !openable || openable.has(id);
  const dropped = requested.filter(id => !canOpen(id));
  const own = link.groupId ? args.ownGroups.find(g => g.id === link.groupId) : undefined;
  if (own) {
    return {
      regionFilter: `group:${own.id}`,
      facts: { requested, shown: [...own.restaurantIds], dropped, groupNotYours: false, groupId: own.id },
    };
  }
  const shown = requested.filter(canOpen);
  const facts: ViewContextLink = {
    requested,
    shown,
    dropped,
    groupNotYours: Boolean(link.groupId),
    groupId: link.groupId,
  };
  if (isOrgFilter(link.filter)) {
    const org = new Set(args.orgVenueIds ?? []);
    const same = requested.length > 0 && requested.length === org.size && requested.every(id => org.has(id));
    // A bare "All venues of" (no ids) is the recipient's own group.
    if (same || !requested.length) {
      return { regionFilter: 'org', facts: { ...facts, shown: [...org] } };
    }
  }
  return { regionFilter: filterForVenues(shown) ?? 'all', facts };
}

/** The venue filter a URL names as written (a group stays a group), or `all`. */
export function urlRegionFilter(search: string): string {
  return decodeOpsUrlState(search).regionFilter ?? 'all';
}

function sameStrings(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b);
  return a.every(x => set.has(x));
}

/**
 * Two contexts that count the same: same venue filter, period, comparison,
 * capacity basis, chart breakdown and filters. A link equal to the person's
 * own saved context is their own view reloaded, not someone else's.
 */
export function sameViewContextState(a: ViewContextState, b: ViewContextState): boolean {
  const pa = coercePerfSettings(a.perfSettings);
  const pb = coercePerfSettings(b.perfSettings);
  const keys: (keyof PerfSettings)[] = [
    'aggregation',
    'frameCount',
    'frameUnit',
    'dateStart',
    'dateEnd',
    'compareDateStart',
    'compareDateEnd',
    'capacityBasis',
  ];
  return (
    (a.regionFilter ?? 'all') === (b.regionFilter ?? 'all') &&
    keys.every(key => (pa[key] ?? null) === (pb[key] ?? null)) &&
    effectiveCompareKind(pa) === effectiveCompareKind(pb) &&
    sameStrings(a.status, b.status) &&
    sameStrings(a.source, b.source) &&
    sameStrings(a.shifts, b.shifts)
  );
}

/** The context a link (or a reload) names, or null when the URL names none. */
export function viewContextFromUrl(search: string): ViewContextState | null {
  if (!urlCarriesViewContext(search)) return null;
  const decoded = decodeOpsUrlState(search);
  return {
    regionFilter:
      decoded.compareFilters.length >= 2
        ? 'all'
        : linkRegionFilter(linkVenuesFromUrl(search), decoded.regionFilter ?? 'all'),
    perfSettings: decoded.perfSettings,
    status: decoded.status,
    source: decoded.source,
    shifts: decoded.shifts ?? [],
  };
}

// ---------------------------------------------------------------------------
// Storage: per person and customer (org)
// ---------------------------------------------------------------------------

export const VIEW_CONTEXT_STORAGE_PREFIX = 'insights:viewContext:v1:';
/** Which org each restaurant belongs to, so a reload knows it before the server answers. */
export const VIEW_CONTEXT_ORG_OF_KEY = 'insights:viewContext:orgOf';
const ORG_OF_LIMIT = 200;

/** The former global keys (audit 3.6): moved into the first org opened, then removed. */
export const LEGACY_VIEW_CONTEXT_KEYS = {
  source: 'aleno_ops_source',
  status: 'aleno_ops_status',
  shifts: 'aleno_ops_shifts',
  perfSettings: 'aleno_ops_perf_settings',
} as const;

/**
 * The venues last chosen for this customer (decision 7.7.1), with the
 * restaurant they were chosen at: a restaurant opens on that choice when it is
 * the same restaurant, else on itself. `insights:lastScope:<user>` still picks
 * which restaurant opens when the URL names none.
 */
export type SavedVenues = { restaurantId: string; regionFilter: string | null };

/** What is kept per person and org. */
export type SavedViewContext = {
  v: typeof VIEW_CONTEXT_VERSION;
  savedAt: string;
  perfSettings?: PerfSettings;
  status?: ReservationStatusGroup[];
  source?: ReservationSourceId[];
  shifts?: string[];
  venues?: SavedVenues;
};

/** The stored parts: the period and the comparison live in `perfSettings`. */
export type SavedPart = 'perfSettings' | 'status' | 'source' | 'shifts' | 'venues';

/** The saved venue choice for this restaurant, if the person made one here. */
export function savedVenuesFor(saved: SavedViewContext | null, restaurantId: string): string | null {
  const venues = saved?.venues;
  return venues && venues.restaurantId === restaurantId ? venues.regionFilter : null;
}

export function viewContextStorageKey(ownerId: string, orgId: string): string {
  return `${VIEW_CONTEXT_STORAGE_PREFIX}${ownerId}:${orgId}`;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readJson(key: string): unknown {
  try {
    const raw = storage()?.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    storage()?.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or private mode: the context still works for this session.
  }
}

function stringList<T extends string>(value: unknown): T[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is T => typeof item === 'string' && !!item.trim());
}

function savedFrom(raw: unknown): SavedViewContext | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const perf = record.perfSettings;
  return {
    v: VIEW_CONTEXT_VERSION,
    savedAt: typeof record.savedAt === 'string' ? record.savedAt : '',
    ...(perf && typeof perf === 'object'
      ? { perfSettings: coercePerfSettings({ ...DEFAULT_PERF_SETTINGS, ...(perf as Partial<PerfSettings>) }) }
      : {}),
    ...(stringList(record.status) ? { status: stringList<ReservationStatusGroup>(record.status) } : {}),
    ...(stringList(record.source) ? { source: stringList<ReservationSourceId>(record.source) } : {}),
    ...(stringList(record.shifts) ? { shifts: stringList<string>(record.shifts) } : {}),
    ...(savedVenuesFrom(record.venues) ? { venues: savedVenuesFrom(record.venues)! } : {}),
  };
}

function savedVenuesFrom(raw: unknown): SavedVenues | null {
  if (!raw || typeof raw !== 'object') return null;
  const { restaurantId, regionFilter } = raw as Partial<SavedVenues>;
  if (typeof restaurantId !== 'string' || !restaurantId) return null;
  if (regionFilter != null && typeof regionFilter !== 'string') return null;
  return { restaurantId, regionFilter: regionFilter ?? 'all' };
}

export function readSavedViewContext(ownerId: string, orgId: string): SavedViewContext | null {
  if (!ownerId || !orgId) return null;
  return savedFrom(readJson(viewContextStorageKey(ownerId, orgId)));
}

/** Save some parts for this person and org; the parts not given stay as saved. */
export function saveViewContextParts(
  ownerId: string,
  orgId: string,
  parts: Partial<Pick<SavedViewContext, SavedPart>>,
): SavedViewContext | null {
  if (!ownerId || !orgId) return null;
  const next: SavedViewContext = {
    ...(readSavedViewContext(ownerId, orgId) ?? { v: VIEW_CONTEXT_VERSION, savedAt: '' }),
    ...parts,
    v: VIEW_CONTEXT_VERSION,
    savedAt: new Date().toISOString(),
  };
  writeJson(viewContextStorageKey(ownerId, orgId), next);
  return next;
}

/** The parts of a saved context, over the defaults (venues are not saved here). */
export function stateFromSaved(
  saved: SavedViewContext | null,
): Omit<ViewContextState, 'regionFilter'> {
  return {
    perfSettings: saved?.perfSettings ?? DEFAULT_PERF_SETTINGS,
    status: saved?.status ?? [],
    source: saved?.source ?? [],
    shifts: saved?.shifts ?? [],
  };
}

export function knownOrgOf(restaurantId: string | null | undefined): string | null {
  if (!restaurantId) return null;
  const map = readJson(VIEW_CONTEXT_ORG_OF_KEY);
  if (!map || typeof map !== 'object') return null;
  const org = (map as Record<string, unknown>)[restaurantId];
  return typeof org === 'string' && org ? org : null;
}

export function rememberOrgOf(restaurantId: string, orgId: string): void {
  if (!restaurantId || !orgId || knownOrgOf(restaurantId) === orgId) return;
  const map = (readJson(VIEW_CONTEXT_ORG_OF_KEY) as Record<string, string> | null) ?? {};
  const { [restaurantId]: _previous, ...rest } = map;
  const kept = Object.entries(rest).slice(-(ORG_OF_LIMIT - 1));
  writeJson(VIEW_CONTEXT_ORG_OF_KEY, { ...Object.fromEntries(kept), [restaurantId]: orgId });
}

/** The former global values, if any are left (read only). */
export function readLegacyViewContext(): SavedViewContext | null {
  const perf = readJson(LEGACY_VIEW_CONTEXT_KEYS.perfSettings);
  const status = readJson(LEGACY_VIEW_CONTEXT_KEYS.status);
  const source = readJson(LEGACY_VIEW_CONTEXT_KEYS.source);
  const shifts = readJson(LEGACY_VIEW_CONTEXT_KEYS.shifts);
  if (perf == null && status == null && source == null && shifts == null) return null;
  return savedFrom({ perfSettings: perf ?? undefined, status, source, shifts });
}

/**
 * Move the former global values into this person's context for this org,
 * once: the first org opened after the change keeps them, the others start
 * from the defaults, as each org's context is its own from now on.
 */
export function migrateLegacyViewContext(ownerId: string, orgId: string): SavedViewContext | null {
  const legacy = readLegacyViewContext();
  if (!legacy || !ownerId || !orgId) return null;
  const { v: _v, savedAt: _at, ...parts } = legacy;
  const saved = saveViewContextParts(ownerId, orgId, parts);
  for (const key of Object.values(LEGACY_VIEW_CONTEXT_KEYS)) {
    try {
      storage()?.removeItem(key);
    } catch {
      // Ignore: the next migration attempt finds an org record and stops.
    }
  }
  return saved;
}

const PERIOD_FIELDS = ['aggregation', 'frameCount', 'frameUnit', 'dateStart', 'dateEnd', 'capacityBasis'] as const;
const COMPARE_FIELDS = ['compareKind', 'compareExplicit', 'compareDateStart', 'compareDateEnd'] as const;

function pickFields(
  s: PerfSettings,
  fields: readonly (keyof PerfSettings)[],
): Partial<PerfSettings> {
  const out: Partial<PerfSettings> = {};
  for (const field of fields) {
    if (s[field] !== undefined) (out as Record<string, unknown>)[field] = s[field];
  }
  return out;
}

function fieldsDiffer(a: PerfSettings, b: PerfSettings, fields: readonly (keyof PerfSettings)[]): boolean {
  return fields.some(field => (a[field] ?? null) !== (b[field] ?? null));
}

/**
 * The period settings to save when a link's context is on screen and the
 * person changes the period or the comparison: only what they changed
 * becomes theirs; the link's other half never overwrites their saved one.
 */
export function mergeChangedPerfSettings(
  saved: PerfSettings | undefined,
  previous: PerfSettings,
  next: PerfSettings,
): PerfSettings {
  const base = saved ?? DEFAULT_PERF_SETTINGS;
  const period = fieldsDiffer(previous, next, PERIOD_FIELDS) ? pickFields(next, PERIOD_FIELDS) : pickFields(base, PERIOD_FIELDS);
  const compare = fieldsDiffer(previous, next, COMPARE_FIELDS) ? pickFields(next, COMPARE_FIELDS) : pickFields(base, COMPARE_FIELDS);
  return coercePerfSettings({ ...DEFAULT_PERF_SETTINGS, ...period, ...compare });
}
